import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Building2, MapPin, ShieldCheck, Home, Landmark } from "lucide-react";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";

const FALLBACK = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80";

export default function PropertyMarketplace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const r = await api.get("/property"); setItems(r.data); }
    catch {} finally { setLoading(false); }
  })(); }, []);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="thom-seal"><Building2 className="h-3 w-3" /> PropertyVerify™ · Beta</span>
            <h1 className="display text-4xl md:text-5xl mt-3">Verified property listings.</h1>
            <p className="text-muted-foreground max-w-2xl mt-2">
              Title deeds, rates clearance, structural inspection & boundary verification — powered by the same trust engine as AutoVerify™.
            </p>
          </div>
          <div className="text-sm text-muted-foreground"><span className="mono">{items.length}</span> listing(s)</div>
        </div>

        {loading ? <div className="text-muted-foreground text-sm">Loading listings…</div> : items.length === 0 ? (
          <div className="p-16 border border-border text-center">
            <Landmark className="h-8 w-8 mx-auto text-primary mb-3" />
            <div className="display text-2xl">PropertyVerify™ is opening for early sellers.</div>
            <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
              Same platform. Same trust engine. Independent module — with property-specific rules for title deeds, structural,
              plumbing/electrical & boundary verification.
            </p>
            <Link to="/register" className="btn-primary mt-6 inline-flex">Become a listing partner</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(p => (
              <div key={p.id} className="border border-border bg-card">
                <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                  <img src={p.photos?.[0] ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.photos[0]}?v=medium` : FALLBACK} alt={p.title} className="w-full h-full object-cover" />
                  {p.verification_status === "verified" ? (
                    <Link
                      to={p.certificate_number ? `/verify/${p.certificate_number}` : "#"}
                      className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm rounded-full p-1 shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label="View THOM VERIFIED certificate"
                    >
                      <ThomVerifiedStamp
                        size={56}
                        verificationStatus="verified"
                        certificateId={p.certificate_number}
                        trustScore={p.trust_score}
                        clickable={false}
                        showTooltip
                      />
                    </Link>
                  ) : (
                    <div className="absolute top-2 left-2">
                      <ThomVerifiedStamp size="xs" verificationStatus={p.verification_status || "unverified"} />
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-border">
                  <div className="display text-lg leading-tight">{p.title}</div>
                  <div className="mt-1 mono text-primary font-semibold">${Number(p.price_usd).toLocaleString()}</div>
                  <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-3 w-3" /> {p.city}{p.suburb ? ` · ${p.suburb}` : ""} · <Home className="h-3 w-3" /> {p.property_type.replace(/_/g, " ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
